import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, UserRound, X } from 'lucide-react';
import type { Facilitator } from '@business-game/shared';
import { facilitatorsApi } from '../../services/api/facilitators.api';

type Props = {
  value: string;
  onChange: (name: string) => void;
  label?: string;
  placeholder?: string;
  compact?: boolean;
};

const getInitials = (name: string) =>
  name
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

export const FacilitatorPicker: React.FC<Props> = ({
  value,
  onChange,
  label = 'Animateur',
  placeholder = 'Rechercher ou créer un animateur…',
  compact = false,
}) => {
  const [facilitators, setFacilitators] = useState<Facilitator[]>([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const list = await facilitatorsApi.getAllFacilitators();
        setFacilitators(Array.isArray(list) ? list : []);
      } catch (error) {
        console.error('Failed to load facilitators:', error);
      }
    };
    void load();
  }, []);

  useEffect(() => {
    const onClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  const trimmedQuery = query.trim();

  const results = useMemo(() => {
    const items = facilitators ?? [];
    if (!trimmedQuery) return items;
    const q = trimmedQuery.toLowerCase();
    return items.filter((f) => f.name.toLowerCase().includes(q));
  }, [facilitators, trimmedQuery]);

  const exactMatch = useMemo(() => {
    const items = facilitators ?? [];
    return items.find((f) => f.name.toLowerCase() === trimmedQuery.toLowerCase());
  }, [facilitators, trimmedQuery]);

  const selectFacilitator = (name: string) => {
    onChange(name);
    setQuery('');
    setOpen(false);
  };

  const createFacilitator = async () => {
    if (!trimmedQuery || exactMatch || creating) return;
    setCreating(true);
    try {
      const created = await facilitatorsApi.createFacilitator({ name: trimmedQuery });
      setFacilitators((current) => {
        const items = current ?? [];
        const exists = items.some((f) => f.id === created.id);
        return exists ? items : [...items, created].sort((a, b) => a.name.localeCompare(b.name, 'fr'));
      });
      selectFacilitator(created.name);
    } catch (error) {
      console.error('Failed to create facilitator:', error);
    } finally {
      setCreating(false);
    }
  };

  const fieldPadding = compact ? 'h-[50px] py-0' : 'py-3.5';
  const labelClass = 'mb-2 block text-[11px] font-bold uppercase tracking-[0.08em] text-slate-400';

  return (
    <div ref={containerRef} className="relative">
      {label && <label className={labelClass}>{label}</label>}

      {value && !open ? (
        <button
          type="button"
          onClick={() => setOpen(true)}
          className={[
            'flex w-full items-center gap-2.5 rounded-[13px] border-[1.5px] border-slate-200 bg-white text-left transition-colors hover:border-indigo-300',
            compact ? 'h-[50px] px-[15px]' : 'px-4 py-3.5',
          ].join(' ')}
        >
          <span className="flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-xs font-bold text-indigo-600">
            {getInitials(value)}
          </span>
          <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-slate-900">{value}</span>
          <span
            role="button"
            tabIndex={0}
            onClick={(e) => {
              e.stopPropagation();
              onChange('');
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.stopPropagation();
                onChange('');
              }
            }}
            className="rounded-lg p-1 text-slate-400 transition-colors hover:bg-slate-200 hover:text-slate-700"
            aria-label="Effacer l'animateur"
          >
            <X className="h-3.5 w-3.5" />
          </span>
        </button>
      ) : (
        <div className="relative">
          <span className="pointer-events-none absolute left-[15px] top-1/2 flex h-[26px] w-[26px] -translate-y-1/2 items-center justify-center rounded-lg bg-slate-100 text-xs font-bold text-slate-500">
            {trimmedQuery ? getInitials(trimmedQuery) : <UserRound className="h-3.5 w-3.5" />}
          </span>
          <input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                if (exactMatch) selectFacilitator(exactMatch.name);
                else if (results.length === 1) selectFacilitator(results[0].name);
                else void createFacilitator();
              }
              if (e.key === 'Escape') setOpen(false);
            }}
            placeholder={placeholder}
            className={[
              'w-full rounded-[13px] border-[1.5px] border-slate-200 bg-white pl-[50px] pr-4 text-[15px] font-semibold text-slate-900 transition-colors focus:border-indigo-500 focus:outline-none focus:ring-4 focus:ring-indigo-500/10',
              fieldPadding,
            ].join(' ')}
          />
        </div>
      )}

      {open && (
        <div className="absolute left-0 right-0 z-30 mt-1.5 max-h-60 overflow-y-auto rounded-2xl border border-slate-200 bg-white py-1.5 shadow-xl shadow-slate-900/10">
          {results.length === 0 && !trimmedQuery && (
            <p className="px-4 py-3 text-sm text-slate-500">Aucun animateur enregistré.</p>
          )}
          {results.map((facilitator) => (
            <button
              key={facilitator.id}
              type="button"
              onClick={() => selectFacilitator(facilitator.name)}
              className="flex w-full items-center gap-2.5 px-4 py-2 text-left transition-colors hover:bg-indigo-50"
            >
              <UserRound className="h-4 w-4 shrink-0 text-indigo-500" />
              <span className="truncate text-sm font-bold text-slate-900">{facilitator.name}</span>
            </button>
          ))}
          {trimmedQuery && !exactMatch && (
            <button
              type="button"
              disabled={creating}
              onClick={() => void createFacilitator()}
              className="mt-1 flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-left text-sm font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 disabled:opacity-60"
            >
              <Plus className="h-4 w-4" />
              {creating ? 'Création…' : `Créer « ${trimmedQuery} »`}
            </button>
          )}
        </div>
      )}
    </div>
  );
};
